import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '@/lib/api';
import { streamAiChat } from '@/lib/aiStream';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sparkles, X, Send, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const QUICK_INTENTS = {
  customer_support: ['Track my order', 'Need emergency diesel now', "Today's diesel price", 'Invoice help'],
  driver_help: ['Brief me on my active jobs', 'Delivery proof checklist', 'Route tips for my delivery'],
};

const AI_MODELS = [
  { key: 'gemini', label: 'Gemini' },
  { key: 'claude', label: 'Claude' },
];

// Pure helpers for the streaming message list (kept outside the component
// to keep the render logic simple and independently testable).
const appendDeltaToLast = (list, delta) => {
  const copy = [...list];
  copy[copy.length - 1] = { sender: 'assistant', content: copy[copy.length - 1].content + delta };
  return copy;
};
const replaceLastWith = (list, content) => {
  const copy = [...list];
  copy[copy.length - 1] = { sender: 'assistant', content };
  return copy;
};

const ChatMessages = ({ msgs, mode, onQuickIntent, endRef }) => (
  <div className="flex-1 overflow-y-auto p-3 space-y-2">
    {msgs.length === 0 && (
      <div className="space-y-2 pt-2">
        <p className="text-sm text-muted-foreground px-1">Hi! I'm your FuelKart assistant. Ask me about orders, prices or deliveries.</p>
        <div className="flex flex-wrap gap-1.5">
          {(QUICK_INTENTS[mode] || []).map((qi) => (
            <button key={qi} onClick={() => onQuickIntent(qi)} className="text-xs rounded-full border border-border px-3 py-1.5 hover:bg-muted text-left" data-testid={`ai-quick-${qi.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{qi}</button>
          ))}
        </div>
      </div>
    )}
    {msgs.map((m, i) => (
      <div key={`${i}-${m.sender}`} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.sender === 'user' ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}
          data-testid={`ai-msg-${m.sender}`}>
          {m.content || <Loader2 className="h-4 w-4 animate-spin" />}
        </div>
      </div>
    ))}
    <div ref={endRef} />
  </div>
);

// Floating AI chat bubble (Gemini / Claude selectable) for customer & driver portals.
export const AiChatWidget = ({ mode = 'customer_support' }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [usage, setUsage] = useState(null);
  const [model, setModel] = useState(() => localStorage.getItem('ai_model') || 'gemini');
  const endRef = useRef(null);

  const pickModel = (key) => { setModel(key); localStorage.setItem('ai_model', key); };

  const loadHistory = useCallback(() => {
    api.get('/ai/chat/history', { params: { mode } })
      .then((r) => {
        setMsgs(r.data.messages.map((m) => ({ sender: m.sender, content: m.content })));
        setUsage(r.data.usage || null);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [mode]);

  useEffect(() => { if (open && !loaded) loadHistory(); }, [open, loaded, loadHistory]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, open]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput('');
    setBusy(true);
    setMsgs((m) => [...m, { sender: 'user', content: q }, { sender: 'assistant', content: '' }]);
    setUsage((u) => (u ? { ...u, used: Math.min(u.used + 1, u.limit) } : u));
    try {
      await streamAiChat(q, mode, (delta) => setMsgs((m) => appendDeltaToLast(m, delta)), model);
    } catch (e) {
      setMsgs((m) => replaceLastWith(m, e.message || 'Something went wrong. Please try again.'));
    }
    setBusy(false);
  };

  const clear = async () => {
    try { await api.delete('/ai/chat/history', { params: { mode } }); setMsgs([]); toast.success('Conversation cleared'); }
    catch (e) { toast.error('Could not clear conversation'); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} aria-label="Open FuelKart AI assistant"
          className="fixed bottom-20 right-4 z-40 h-13 w-13 p-3.5 rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90 active:scale-95 transition-transform flex items-center justify-center"
          data-testid="ai-chat-bubble">
          <Sparkles className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-20 right-2 sm:right-4 z-40 w-[calc(100vw-1rem)] sm:w-[380px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden" style={{ height: 'min(520px, calc(100vh - 12rem))' }} data-testid="ai-chat-panel">
          <div className="flex items-center justify-between px-4 h-12 bg-primary text-primary-foreground shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" /><span className="text-sm font-semibold">FuelKart AI</span>
              <div className="flex items-center rounded-full bg-white/15 p-0.5" data-testid="ai-model-switch">
                {AI_MODELS.map((m) => (
                  <button key={m.key} onClick={() => pickModel(m.key)}
                    className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${model === m.key ? 'bg-white/90 text-primary font-semibold' : 'opacity-80 hover:opacity-100'}`}
                    data-testid={`ai-model-${m.key}`}>{m.label}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {usage && <span className="text-[10px] opacity-75 mr-1" title="Daily AI usage" data-testid="ai-usage-counter">{usage.used}/{usage.limit} today</span>}
              <button onClick={clear} className="p-1.5 rounded hover:bg-white/10" aria-label="Clear conversation" data-testid="ai-chat-clear"><Trash2 className="h-4 w-4" /></button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-white/10" aria-label="Close chat" data-testid="ai-chat-close"><X className="h-4 w-4" /></button>
            </div>
          </div>
          <ChatMessages msgs={msgs} mode={mode} onQuickIntent={send} endRef={endRef} />
          <form className="p-2 border-t border-border flex gap-2 shrink-0" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything..." disabled={busy} className="h-10" data-testid="ai-chat-input" />
            <Button type="submit" size="icon" className="h-10 w-10 shrink-0" disabled={busy || !input.trim()} data-testid="ai-chat-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      )}
    </>
  );
};
